import { read, utils, write } from "xlsx";
import { zipSync, unzipSync, strToU8, strFromU8 } from "fflate";
import { LMS_QUESTION_TYPES } from "./constants";

const HEADERS = ["questionText", "questionType", "option1", "option2", "option3", "option4", "correctAnswer", "marks"];

const BASE_HEADER_LABELS = {
  questionText: "Question",
  questionType: "Question type",
  correctAnswer: "Correct answer",
  marks: "Marks",
};

const HEADER_LABELS = Object.fromEntries(
  HEADERS.map((header) => {
    if (/^option\d+$/.test(header)) {
      const position = Number(header.slice(6));
      return [header, `Option ${position}(${columnLetter(position - 1)})`];
    }
    return [header, BASE_HEADER_LABELS[header]];
  }),
);

const LABEL_TO_HEADER = Object.fromEntries(
  Object.entries(HEADER_LABELS).map(([header, label]) => [label.toLowerCase(), header]),
);
const LABEL_TO_TYPE = Object.fromEntries(
  LMS_QUESTION_TYPES.map(({ label, value }) => [label.toLowerCase(), value]),
);
const TYPE_LABEL = (value) => LMS_QUESTION_TYPES.find((type) => type.value === value)?.label ?? value;
const TYPE_LABELS = LMS_QUESTION_TYPES.map(({ label }) => label);

const QUESTION_TYPE_COLUMN = HEADERS.indexOf("questionType");
const VALIDATION_LAST_ROW = 1000;
const cellText = (value) => String(value ?? "").trim();

function columnLetter(index) {
  let letter = "";
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letter = String.fromCharCode(65 + ((n - 1) % 26)) + letter;
  }
  return letter;
}

function toInternalHeader(raw) {
  const text = cellText(raw);
  if (!text) return "";
  return LABEL_TO_HEADER[text.toLowerCase()] || (HEADERS.includes(text) ? text : null);
}

function resolveQuestionType(raw) {
  const text = cellText(raw);
  return LABEL_TO_TYPE[text.toLowerCase()] || text;
}

const normalizeAnswer = (value) => cellText(value).toLowerCase().replace(/\s+/g, " ");

function matchOptionIndex(options, answer) {
  const target = normalizeAnswer(answer);
  if (!target) return -1;
  const direct = options.findIndex((option) => normalizeAnswer(option) === target);
  if (direct >= 0) return direct;
  const letter = /^([a-z])[.)]?$/.exec(target);
  if (letter) {
    const index = letter[1].charCodeAt(0) - 97;
    return index < options.length ? index : -1;
  }
  return -1;
}

export function createQuizTemplate() {
  const workbook = utils.book_new();
  const sheet = utils.aoa_to_sheet([
    HEADERS.map((header) => HEADER_LABELS[header]),
    ["What is 2 + 2?", TYPE_LABEL("MCQ"), "2", "3", "4", "5", "4", 1],
    ["The Earth orbits the Sun.", TYPE_LABEL("True/False"), "", "", "", "", "True", 1],
    ["Name the planet we live on.", TYPE_LABEL("Short Answer"), "", "", "", "", "Earth", 2],
  ]);
  sheet["!cols"] = HEADERS.map((header) => ({ wch: header === "questionText" ? 44 : header === "questionType" ? 18 : 16 }));
  utils.book_append_sheet(workbook, sheet, "Questions");
  const instructions = utils.aoa_to_sheet([
    ["Quiz question import instructions"],
    ["Replace the sample rows in Questions with your questions. Keep the column headings."],
    ["Pick a value from the Question type dropdown: Multiple choice, True / False, or Short answer."],
    ["Multiple choice: enter at least two options. Correct answer can be the option text (case-insensitive) or its letter, e.g. A, B or C."],
    ["Option columns may be labelled Option 1(A), Option 1, or simply A, B, C."],
    ["Add Option 5(E), Option 6(F), etc. columns if you need more choices."],
    ["True / False: Correct answer must be True or False."],
    ["Short answer: Correct answer may be blank for manual marking."],
    ["Marks must be a positive whole number. Blank marks default to 1."],
    ["Use plain values, not formulas. Blank rows are ignored."],
    ["Import, review the preview, add to quiz, then save the quiz."],
  ]);
  instructions["!cols"] = [{ wch: 100 }];
  utils.book_append_sheet(workbook, instructions, "Instructions");
  return workbook;
}

function injectQuestionTypeValidation(xml) {
  const column = columnLetter(QUESTION_TYPE_COLUMN);
  const validation = `<dataValidations count="1"><dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1" sqref="${column}2:${column}${VALIDATION_LAST_ROW}"><formula1>"${TYPE_LABELS.join(",")}"</formula1></dataValidation></dataValidations>`;
  return xml.replace("</sheetData>", `</sheetData>${validation}`);
}

function injectHeaderStyle(stylesXml) {
  const count = (tag) => Number(new RegExp(`<${tag} count="(\\d+)"`).exec(stylesXml)[1]);
  const fontId = count("fonts");
  const fillId = count("fills");
  const styleIndex = count("cellXfs");
  const font = '<font><b/><sz val="11"/><color rgb="FF1F4E78"/><name val="Calibri"/><family val="2"/><scheme val="minor"/></font>';
  const fill = '<fill><patternFill patternType="solid"><fgColor rgb="FFD9E1F2"/><bgColor indexed="64"/></patternFill></fill>';
  const cellFormat = `<xf numFmtId="0" fontId="${fontId}" fillId="${fillId}" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>`;
  const xml = stylesXml
    .replace("</fonts>", `${font}</fonts>`)
    .replace("</fills>", `${fill}</fills>`)
    .replace("</cellXfs>", `${cellFormat}</cellXfs>`)
    .replace(/<fonts count="\d+"/, `<fonts count="${fontId + 1}"`)
    .replace(/<fills count="\d+"/, `<fills count="${fillId + 1}"`)
    .replace(/<cellXfs count="\d+"/, `<cellXfs count="${styleIndex + 1}"`);
  return { xml, styleIndex };
}

function styleHeaderRow(xml, styleIndex) {
  return xml.replace(/<c r="([A-Z]+1)"/g, `<c r="$1" s="${styleIndex}"`);
}

function buildTemplateBytes() {
  const workbook = createQuizTemplate();
  const files = unzipSync(new Uint8Array(write(workbook, { bookType: "xlsx", type: "array" })));
  const sheetPath = `xl/worksheets/sheet${workbook.SheetNames.indexOf("Questions") + 1}.xml`;
  const { xml: stylesXml, styleIndex } = injectHeaderStyle(strFromU8(files["xl/styles.xml"]));
  files["xl/styles.xml"] = strToU8(stylesXml);
  if (files[sheetPath]) {
    const sheetXml = styleHeaderRow(injectQuestionTypeValidation(strFromU8(files[sheetPath])), styleIndex);
    files[sheetPath] = strToU8(sheetXml);
  }
  return zipSync(files);
}

export function downloadQuizTemplate() {
  const url = URL.createObjectURL(new Blob([buildTemplateBytes()], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "quiz-questions-template.xlsx";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function parseQuizWorkbook(data) {
  const workbook = read(data, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames.includes("Questions") ? "Questions" : workbook.SheetNames[0]];
  if (!sheet) throw new Error("The workbook has no worksheet.");
  const rows = utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: true, raw: false });
  const rawHeaders = (rows[0] || []).map(cellText);
  const headers = rawHeaders.map(toInternalHeader);
  for (const header of ["questionText", "questionType", "correctAnswer"]) {
    if (!headers.includes(header)) throw new Error(`Missing column: ${HEADER_LABELS[header]}. Use the Excel template.`);
  }
  const mappedHeaders = headers.filter(Boolean);
  if (mappedHeaders.some((header, index, values) => values.indexOf(header) !== index)) {
    throw new Error("Column headings must be unique.");
  }
  const optionsColumns = rawHeaders.map((header, index) => {
    const match = /^(?:option\s*(\d+)(?:\s*\(([a-z])\))?|([a-z]))$/i.exec(header);
    if (!match) return null;
    const number = match[1] ? Number(match[1]) : match[3].toLowerCase().charCodeAt(0) - 96;
    return { number, index };
  }).filter(Boolean).sort((a, b) => a.number - b.number);
  const questions = [];
  rows.slice(1).forEach((row, index) => {
    if (row.every((value) => !cellText(value))) return;
    const fail = (reason) => { throw new Error(`Row ${index + 2}: ${reason}`); };
    const offset = utils.decode_range(sheet["!ref"]).s;
    rawHeaders.forEach((header, column) => {
      if (header && sheet[utils.encode_cell({ r: offset.r + index + 1, c: offset.c + column })]?.f) fail("Use plain values instead of formulas.");
    });
    const value = (header) => cellText(row[headers.indexOf(header)]);
    const questionText = value("questionText");
    const questionType = resolveQuestionType(value("questionType"));
    const correctAnswer = value("correctAnswer");
    const marksText = value("marks");
    const marks = marksText ? Number(marksText) : 1;
    const options = optionsColumns.map(({ index: column }) => cellText(row[column])).filter(Boolean);
    if (!questionText) fail("Question is required.");
    if (!LMS_QUESTION_TYPES.some(({ value: type }) => type === questionType)) fail("Question type must be Multiple choice, True / False, or Short answer.");
    if (!Number.isSafeInteger(marks) || marks < 1) fail("Marks must be a positive whole number.");
    let answer = correctAnswer;
    if (questionType === "MCQ") {
      if (options.length < 2) fail("MCQ needs at least two options.");
      const answerIndex = matchOptionIndex(options, correctAnswer);
      if (answerIndex < 0) fail(`Correct answer "${correctAnswer}" must match an option or be an option letter such as A, B, C or D.`);
      answer = options[answerIndex];
    }
    if (questionType === "True/False" && !["True", "False"].includes(correctAnswer)) fail("Correct answer must be True or False.");
    questions.push({ questionText, questionType, correctAnswer: answer, marks, optionFields: questionType === "MCQ" ? options : [], order: questions.length + 1 });
  });
  if (!questions.length) throw new Error("The worksheet must contain at least one question.");
  return questions;
}
